import styled, { css } from 'styled-components'

import { colors } from 'app/theme/colors'
import { radius, space } from 'app/theme/tokens'

/* The table is a CSS grid rather than <table>, because each row expands into a
   second grid of enrolment lines and a colspan'd <tr> fights the layout. */
const COLUMNS = '1.1fr 1.3fr 0.9fr 1.1fr 1.2fr 1.2fr'

export const Wrapper = styled.div`
  padding: ${space.md};
  display: flex;
  flex-direction: column;
  gap: ${space.md};
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${space.sm};
`

export const AsOf = styled.span`
  font-size: 12px;
  color: ${colors.textSecondary};
  border-bottom: 1px dotted ${colors.grayMedium};
  cursor: help;
`

export const Totals = styled.div`
  display: flex;
  gap: ${space.md};
  font-size: 13px;
  color: ${colors.textSecondary};
`

export const Danger = styled.span`
  color: #c62828;
  font-family: AvenirDemi;
  font-size: 12px;
`

export const Filters = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${space.sm};
`

export const TableHead = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: ${space.sm};
  padding: 0 ${space.sm} ${space.xs};
  font-family: AvenirDemi;
  font-size: 11px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${colors.textSecondary};
  border-bottom: 1px solid ${colors.borderDefault};
`

export const Row = styled.div`
  border: 1px solid ${colors.borderDefault};
  border-radius: ${radius.md};
  padding: ${space.sm};
  background: ${colors.white};

  /* A parent about to lose their classes is the one thing on this board that
     must not be skimmed past. */
  ${(props) =>
    props.$terminating &&
    css`
      border-left: 4px solid #c62828;
      background: #fffafa;
    `}
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: ${space.sm};
  align-items: start;
`

export const Cell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const ParentName = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-family: AvenirDemi;
  font-size: 14px;
  color: ${colors.textPrimary};
`

export const Sub = styled.div`
  font-size: 11px;
  color: ${colors.textTertiary};
  overflow: hidden;
  text-overflow: ellipsis;
`

export const Summary = styled.div`
  font-size: 12px;
  color: ${colors.textPrimary};
  overflow-wrap: anywhere;
`

const TONES = {
  book: '#1565c0',
  terminate: '#c62828',
  done: '#2e7d32',
  muted: colors.grayDark
}

export const Outcome = styled.div`
  font-family: AvenirDemi;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${(props) => TONES[props.$tone] || colors.grayDark};
`

export const MoneyLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${space.sm};
  font-size: 12px;
  color: ${colors.textSecondary};

  ${(props) =>
    props.$total &&
    css`
      border-top: 1px solid ${colors.borderDefault};
      margin-top: 2px;
      padding-top: 3px;
      color: ${colors.textPrimary};
    `}
`

/* The answer to "why is this parent's discount what it is" — the single most
   asked question of the September cycle. */
export const Why = styled.div`
  font-size: 11px;
  color: ${colors.textSecondary};
  font-style: italic;
  margin-top: 2px;
`

export const CommentFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.xs};
  font-size: 10px;
  color: ${colors.textTertiary};
`

export const Lines = styled.div`
  margin-top: ${space.sm};
  border-top: 1px dashed ${colors.borderDefault};
  padding-top: ${space.sm};
`

const LINE_COLUMNS = '1fr 1.4fr 1fr 0.7fr 0.7fr 0.7fr 0.7fr 0.7fr'

export const LineHead = styled.div`
  display: grid;
  grid-template-columns: ${LINE_COLUMNS};
  gap: ${space.xs};
  font-family: AvenirDemi;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${colors.textTertiary};
  padding-bottom: 3px;
`

export const Line = styled.div`
  display: grid;
  grid-template-columns: ${LINE_COLUMNS};
  gap: ${space.xs};
  font-size: 12px;
  color: ${colors.textSecondary};
  padding: 3px 0;
  border-top: 1px solid ${colors.ohBorderGray};
`

export const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${space.md};
  font-size: 12px;
  color: ${colors.textSecondary};
  padding-top: ${space.sm};
`

export const Loading = styled.div`
  display: flex;
  justify-content: center;
  padding: ${space.xl};
`

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space.xs};
  padding: ${space.xl};
  color: ${colors.textSecondary};
  text-align: center;
`

/* "Review" names what the dropdown is: whether a HUMAN has looked at the row.
   Unlabelled, a bare "Pending" sat beside a green ALREADY BOOKED and read as a
   contradiction — the first thing a reader asked about this board. */
export const ReviewLabel = styled.div`
  margin-top: ${space.xs};
  font-family: AvenirDemi;
  font-size: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.textTertiary};
`

export const Settled = styled.div`
  font-size: 12px;
  font-style: italic;
  color: ${colors.textTertiary};
  padding: ${space.xs} 0;
`

export const Charged = styled.div`
  font-family: AvenirDemi;
  font-size: 11px;
  color: #2e7d32;
  display: flex;
  flex-direction: column;
`

export const ParentLink = styled.a`
  color: ${colors.textPrimary};
  text-decoration: none;
  border-bottom: 1px dotted ${colors.grayMedium};

  &:hover {
    color: ${colors.ohOrange};
    border-bottom-color: ${colors.ohOrange};
  }
`
